import React, {useEffect, useRef, useState, useCallback} from 'react'

const TextEditingOverlay = ({runtime}) => {
    const [session, setSession] = useState(null)
    const textareaRef = useRef(null)
    const committingRef = useRef(false)

    useEffect(() => {
        if (!runtime?.subscribeUiState) return
        setSession(runtime.getUiState().textEditing)
        const unsubscribe = runtime.subscribeUiState((uiState) => {
            setSession(uiState.textEditing)
        })
        return unsubscribe
    }, [runtime])

    useEffect(() => {
        if (session && textareaRef.current) {
            textareaRef.current.focus()
            const len = textareaRef.current.value.length
            textareaRef.current.setSelectionRange(len, len)
        }
    }, [session?.nodeId])

    const autoResize = useCallback(() => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = el.scrollHeight + 'px'
    }, [])

    useEffect(() => {
        autoResize()
    }, [session?.draftText, autoResize])

    useEffect(() => {
        if (!session) return
        function onPointerDown(e) {
            if (textareaRef.current && textareaRef.current.contains(e.target)) return
            if (committingRef.current) return
            committingRef.current = true
            runtime.commitTextEditing()
            committingRef.current = false
        }
        document.addEventListener('pointerdown', onPointerDown, true)
        return () => document.removeEventListener('pointerdown', onPointerDown, true)
    }, [session, runtime])

    if (!session) return null

    const node = runtime.getState().nodes.get(session.nodeId)
    const style = node?.style || {}
    const {rect} = session

    const handleChange = (e) => {
        runtime.updateDraftText(e.target.value)
    }

    const handleBlur = () => {
        if (committingRef.current) return
        committingRef.current = true
        runtime.commitTextEditing()
        committingRef.current = false
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            runtime.cancelTextEditing()
        }
    }

    return (
        <textarea
            ref={textareaRef}
            value={session.draftText}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={{
                position: 'absolute',
                left: rect.x,
                top: rect.y,
                width: rect.width,
                minHeight: rect.height,
                fontFamily: style.fontFamily ?? 'Arial',
                fontSize: (style.fontSize ?? 16) * (runtime.getState().viewport?.scale ?? 1),
                color: style.fill ?? '#000',
                textAlign: style.align ?? 'left',
                background: 'transparent',
                border: '1px solid #4a90d9',
                outline: 'none',
                resize: 'none',
                overflow: 'hidden',
                padding: 0,
                margin: 0,
                lineHeight: 'normal',
                boxSizing: 'border-box',
                zIndex: 1000,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
        />
    )
}

export default TextEditingOverlay
